<template>
  <div id="profile" class="flex flex-col gap-6 proxima_nova">
    <h1 class="text-xl text_color font-semibold">Profile</h1>

    <div class="flex flex-col gap-6 secondary_color text-[13px] font-normal">
      <div class="flex flex-row items-start gap-4">
        <div class="flex flex-col items-center gap-1 shrink-0">
          <div class="relative group cursor-pointer" @click="triggerProfilePicUpload">
            <div
              v-if="profilePicUrl"
              class="w-16 h-16 rounded-full overflow-hidden"
            >
              <img :src="profilePicUrl" alt="profile pic" class="w-full h-full object-cover" />
            </div>
            <div
              v-else
              class="w-16 h-16 rounded-full flex items-center justify-center"
              :style="{ backgroundColor: formState.avatar_color || 'var(--white-opacity-8)' }"
            >
              <span class="text-lg font-semibold text-white">{{ userInitials }}</span>
            </div>
            <div class="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
              <ThemeImage :icon="imageNames.CAMERA_GREY || imageNames.UPLOAD_GREY || imageNames.EDIT_ICON" alt="upload" class="size-5" />
            </div>
            <input
              ref="profilePicInput"
              type="file"
              accept="image/*"
              class="hidden"
              @change="handleProfilePicSelect"
            />
          </div>
          <span
            v-if="isUploadingProfilePic"
            class="text-[10px] secondary_color"
          >Uploading...</span>
        </div>

        <div class="flex flex-col gap-2 flex-1">
          <div class="grid grid-cols-2 gap-2">
            <div id="first_name" class="form_col">
              <span>First Name</span>
              <a-input
                v-model:value="formState.first_name"
                class="input_medium"
                placeholder="Enter First Name"
                ref="firstNameInputRef"
                allow-clear
              />
            </div>

            <div id="last_name" class="form_col">
              <span>Last Name</span>
              <a-input
                v-model:value="formState.last_name"
                class="input_medium"
                placeholder="Enter Last Name"
                allow-clear
              />
            </div>
          </div>
        </div>
      </div>

      <div id="email" class="form_col">
        <span>Email</span>
        <a-input
          v-model:value="formState.email"
          type="email"
          class="input_medium"
          disabled
        />
      </div>

      <div id="phone_number" class="form_col">
        <span>Phone Number</span>
        <vue-tel-input
          :key="defaultCountryCode"
          v-model="formState.phone_number"
          placeholder="Enter Phone Number"
          :defaultCountry="defaultCountryCode"
          mode="international"
          :dropdownOptions="{
            showFlags: true,
            showDialCodeInList: true,
            showSearchBox: true,
            searchBoxPlaceholder: 'Search country..',
          }"
        />
      </div>

      <div class="form_col">
        <label for="phoneNumber">Timezone</label>
        <a-select v-model:value="formState.timezone" showSearch>
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
          <a-select-option
            v-for="timezone in timeZones"
            :key="`timezone_${timezone}`"
            :value="timezone"
          >
            {{ timezone }}
          </a-select-option>
        </a-select>
      </div>

      <div class="form_col" v-if="lightModeEnabled">
        <label for="phoneNumber">Theme</label>
        <a-select v-model:value="theme">
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
          <a-select-option
            v-for="theme in themeOptions"
            :key="`theme_${theme.value}`"
            :value="theme.value"
          >
            <span class="text_color">{{ theme.label }}</span>
          </a-select-option>
        </a-select>
      </div>

      <div class="form_col">
        <label>Country code for phone numbers</label>
        <a-select
          v-model:value="selectedCountryCode"
          showSearch
          :loading="countriesLoading"
          :filter-option="filterCountryOption"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
          <a-select-option value="default">
            Default workspace country code
          </a-select-option>
          <a-select-option
            v-for="country in countryCodeOptions"
            :key="`country_${country.countryCode}`"
            :value="country.countryCode"
          >
            {{ country.label }} ({{ country.countryCode }})
          </a-select-option>
        </a-select>
      </div>

      <div class="flex flex-col gap-1" v-if="chatsEnabled">
        <div class="flex items-center gap-3">
          <a-switch
            size="small"
            v-model:checked="formState.chat_notifications_muted"
          />
          <span class="text_color text-sm font-semibold">Mute Chat Notifications</span>
        </div>
        <span class="text-xs font-normal secondary_color ml-10">
          When muted you will not be hearing notification sound when a new message is received
        </span>
      </div>

      <div class="form_col" v-if="salesforce_enabled">
        <span>Salesforce User</span>
        <a-select
          v-model:value="formState.salesforce_user_id"
          showSearch
          placeholder="Search Salesforce users (minimum 3 characters)..."
          :loading="isSearching"
          :filter-option="false"
          @search="onSearch"
          :not-found-content="searchNotFoundContent"
          @select="onSalesforceUserSelect"
        >
          <template #suffixIcon>
            <search-outlined v-if="!isSearching" />
            <loading-outlined v-else />
          </template>
          <a-select-option
            v-for="user in salesforceUsers"
            :key="user.external_id"
            :value="user.external_id"
          >
            {{ user.email }}
          </a-select-option>

          <a-select-option
            v-if="initialSalesforceUser.external_id && !salesforceUsers.length"
            :value="initialSalesforceUser.external_id"
          >
            {{ initialSalesforceUser.email }}
          </a-select-option>
        </a-select>
      </div>

      <a-button @click="onFinish" class="primary_medium w-fit">
        Update changes
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted, nextTick, watch } from "vue";
import { message } from "ant-design-vue";
import { SearchOutlined, LoadingOutlined } from "@ant-design/icons-vue";
import { VueTelInput } from "vue-tel-input";
import DashboardService from "@/src/api/DashboardService.js";
import eventBus from "@/src/utils/eventBus.js";
import { timeZones } from "@/src/constants/timeZoneConstants.js";
import { getTimeZone, fetchCountries } from "@/src/utils/utils.js";
import { focusDelay } from "@/src/constants/commonConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { themeOptions, themeTypes } from "@/src/constants/themeConstants.js";
import store from "@/src/store/index.js";
import { isAppStoreV2, isAppInstalled } from "@/src/utils/appStoreUtils.js";

const defaultCountryCode = computed(() => store.getters["workspace/defaultCountryCode"]);
const workspaceDefaultCountryCode = computed(() => store.getters["workspace/workspaceDefaultCountryCode"]);

const selectedCountryCode = ref("default");
const countryCodeOptions = ref([]);
const countriesLoading = ref(false);
const chatsEnabled = ref(false);

const filterCountryOption = (input, option) => {
  const label = option.children?.[0]?.children || option.value || "";
  return label.toString().toLowerCase().includes(input.toLowerCase());
};

watch(selectedCountryCode, (newValue) => {
  if (newValue === "default") {
    store.dispatch("workspace/setDefaultCountryCode", null);
  } else {
    store.dispatch("workspace/setDefaultCountryCode", newValue);
  }
});

const formState = reactive({
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  timezone: "",
  salesforce_user_id: "",
  salesforce_user_email: "",
  chat_notifications_muted: false,
});

const theme = ref(null);
const firstNameInputRef = ref(null);
const profilePicInput = ref(null);
const profilePicUrl = ref(null);
const isUploadingProfilePic = ref(false);
const loading = ref(true);
const salesforce_enabled = ref(false);
const isSearching = ref(false);
const salesforceUsers = ref([]);
const searchNotFoundContent = ref(
  "Please enter at least 3 characters to search",
);
const initialSalesforceUser = reactive({
  external_id: "",
  email: "",
});

const userInitials = computed(() => {
  return (
    formState.first_name.charAt(0) + formState.last_name.charAt(0)
  ).toUpperCase();
});

const triggerProfilePicUpload = () => {
  profilePicInput.value?.click();
};

const handleProfilePicSelect = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith("image/")) {
    message.error("Please select an image file");
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    message.error("Image must be smaller than 5MB");
    return;
  }

  isUploadingProfilePic.value = true;
  try {
    const uploadData = new FormData();
    uploadData.append("file", file);
    const response = await DashboardService.uploadMarketingFile(uploadData);
    if (response?.success && response?.data?.url) {
      profilePicUrl.value = response.data.url;
      formState.profile_pic = response.data.url;
    } else {
      message.error("Failed to upload image");
    }
  } catch (error) {
    console.error("Failed to upload profile pic:", error);
    message.error("Failed to upload image");
  } finally {
    isUploadingProfilePic.value = false;
    if (profilePicInput.value) profilePicInput.value.value = "";
  }
};

const effectiveTheme = computed(() => store.getters["theme/getEffectiveTheme"]);

const lightModeEnabled = computed(() => { 
  return !!localStorage.getItem("light_theme");
});

let searchTimeout;

const onSearch = (value) => {
  clearTimeout(searchTimeout);

  // Clear results if search is empty
  if (!value) {
    salesforceUsers.value = [];
    searchNotFoundContent.value =
      "Please enter at least 3 characters to search";
    return;
  }

  // Only search if value is 3 or more characters
  if (value.length >= 3) {
    isSearching.value = true;
    searchTimeout = setTimeout(() => {
      searchSalesforceUsers(value);
    }, 300);
    searchNotFoundContent.value = "No users found";
  } else {
    salesforceUsers.value = [];
    searchNotFoundContent.value =
      "Please enter at least 3 characters to search";
  }
};

const onSalesforceUserSelect = (value, option) => {
  // Find the selected user from salesforceUsers
  const selectedUser =
    salesforceUsers.value.find((user) => user.external_id === value) ||
    (initialSalesforceUser.external_id === value
      ? initialSalesforceUser
      : null);

  if (selectedUser) {
    formState.salesforce_user_id = selectedUser.external_id;
    formState.salesforce_user_email = selectedUser.email;
  }
};

const searchSalesforceUsers = async (searchValue) => {
  try {
    const response = await DashboardService.searchSalesforceUsers(searchValue);
    if (response.success && response.users) {
      salesforceUsers.value = response.users;
    }
  } catch (error) {
    console.error("Error searching Salesforce users:", error);
    // message.error("Failed to search Salesforce users");
  } finally {
    isSearching.value = false;
  }
};

const fetchUserProfile = async () => {
  try {
    const response = await DashboardService.getProfile();
    if (response.success && response.profile) {
      Object.assign(formState, response.profile);
      profilePicUrl.value = response.profile.profile_pic || null;
      formState.chat_notifications_muted = !!response.profile.chat_notifications_muted;
      formState.timezone = formState.timezone
        ? formState.timezone
        : getTimeZone();

      store.commit("chats/SET_CHAT_NOTIFICATION_SOUND_ENABLED", !formState.chat_notifications_muted);

      salesforce_enabled.value = response.profile.salesforce_enabled;

      if (
        response.profile.salesforce_user_id &&
        response.profile.salesforce_user_email
      ) {
        initialSalesforceUser.external_id = response.profile.salesforce_user_id;
        initialSalesforceUser.email = response.profile.salesforce_user_email;

        // Add initial user to salesforceUsers array
        salesforceUsers.value = [
          {
            external_id: response.profile.salesforce_user_id,
            email: response.profile.salesforce_user_email,
          },
        ];
      }
    }
  } catch (error) {
    console.error("Error fetching user profile:", error);
    message.error("Failed to fetch user profile");
  } finally {
    loading.value = false;
  }
};

const onFinish = async () => {
  try {
    const payload = { ...formState, theme: theme.value };

    const response = await DashboardService.updateProfile(payload);

    if (response?.success) {
      const firstName = response.profile.first_name || "";
      const lastName = response.profile.last_name || "";
      const userName = `${firstName} ${lastName}`.trim();

      await store.dispatch(
        "theme/setTheme",
        response?.profile?.theme || themeTypes.LIGHT,
      );
      localStorage.removeItem("theme");

      if (userName !== localStorage.getItem("userName")) {
        localStorage.setItem("userName", userName);
        eventBus.emit("user-name-updated", userName);
      }

      // Update the users store so other components (huddle invite, chat list, etc.) see changes
      const currentUserId = store.getters["auth/userData"]?.id;
      if (currentUserId) {
        const userUpdate = {
          id: currentUserId,
          name: userName,
          first_name: firstName,
          last_name: lastName,
        };
        if (response.profile.profile_pic !== undefined) {
          userUpdate.profile_pic = response.profile.profile_pic;
          profilePicUrl.value = response.profile.profile_pic || null;
        }
        store.commit("users/UPDATE_USER", userUpdate);
      }

      await store.dispatch("workspace/refreshUserTimezone");

      store.commit("chats/SET_CHAT_NOTIFICATION_SOUND_ENABLED", !formState.chat_notifications_muted);

      message.success("Profile updated successfully");
    } else {
      message.error(
        "Failed to update profile: " + (response.errors || "Unknown error"),
      );
    }
  } catch (error) {
    console.error("Error updating profile:", error);
    message.error("An error occurred while updating profile");
  }
};

watch(
  () => effectiveTheme.value,
  () => {
    theme.value = effectiveTheme.value;
  },
  {
    immediate: true,
  },
);

onMounted(async () => {
  await store.dispatch("workspace/fetchDefaultCountryCode");
  fetchUserProfile();
  await fetchCountries(countryCodeOptions, countriesLoading);

  if (isAppStoreV2(store)) {
    chatsEnabled.value = await isAppInstalled("chat");
  } else {
    chatsEnabled.value = !!localStorage.getItem("chats");
  }

  if (defaultCountryCode.value !== workspaceDefaultCountryCode.value) {
    selectedCountryCode.value = defaultCountryCode.value;
  }

  nextTick(() => {
    setTimeout(() => {
      if (firstNameInputRef?.value) {
        firstNameInputRef.value?.focus();
      }
    }, focusDelay);
  });
});
</script>

<style scoped>
.form_col {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
